import { createFileRoute } from "@tanstack/react-router";

import { Navbar } from "@/components/landing/Navbar";
import { HeroSection } from "@/components/landing/HeroSection";
import { ProblemSection } from "@/components/landing/ProblemSection";
import { PromiseSection } from "@/components/landing/PromiseSection";
import { SessionsSection } from "@/components/landing/SessionsSection";
import { AudienceSection } from "@/components/landing/AudienceSection";
import { SocialProofSection } from "@/components/landing/SocialProofSection";
import { PricingSection } from "@/components/landing/PricingSection";
import { HowItWorksSection } from "@/components/landing/HowItWorksSection";
import { FormSection } from "@/components/landing/FormSection";
import { FAQSection } from "@/components/landing/FAQSection";
import { FAQ_ITEMS } from "@/components/landing/faq-data";
import { FinalCTASection } from "@/components/landing/FinalCTASection";
import { Footer } from "@/components/landing/Footer";
import { StickyMobileCTA } from "@/components/landing/StickyMobileCTA";

const TITLE = "SPRINT CODE — sua operação com Claude Code e agentes de IA, em 4 sessões";
const DESCRIPTION =
  "Serviço premium e individual: 4 sessões ao vivo de 1h30 para mapear a sua operação, instalar o Claude Code no seu computador e criar os agentes que o seu negócio precisa — numa arquitetura que é sua, pronta para qualquer IA.";
const URL = "https://sprint.campioto.com/";
const OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/fAajhM3sXXbA7Oln6iqVhdTQfC32/social-images/social-1777264918261-Leão_Znith.webp";

const serviceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "SPRINT CODE INDIVIDUAL",
  serviceType: "Programa de implantação de IA para empresas",
  description: DESCRIPTION,
  image: OG_IMAGE,
  url: URL,
  areaServed: "BR",
  availableChannel: {
    "@type": "ServiceChannel",
    serviceUrl: URL,
    availableLanguage: "pt-BR",
  },
  provider: {
    "@type": "Organization",
    name: "ZNITH.AI",
    url: URL,
  },
  offers: {
    "@type": "Offer",
    price: "6870.00",
    priceCurrency: "BRL",
    availability: "https://schema.org/InStock",
    url: URL,
  },
};

const courseJsonLd = {
  "@context": "https://schema.org",
  "@type": "Course",
  name: "SPRINT CODE — 4 sessões",
  description:
    "Quatro sessões ao vivo de 1h30: o mapa da operação, a instalação do Claude Code, os agentes e as skills, e o time de IA rodando.",
  inLanguage: "pt-BR",
  provider: {
    "@type": "Organization",
    name: "ZNITH.AI",
    url: URL,
  },
  hasCourseInstance: {
    "@type": "CourseInstance",
    courseMode: "online",
    courseWorkload: "PT6H",
    instructor: {
      "@type": "Person",
      name: "Leilaine Campioto",
    },
  },
};

const ORG_ID = `${URL}#organization`;
const WEBSITE_ID = `${URL}#website`;
const PERSON_ID = `${URL}#leilaine-campioto`;

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: "ZNITH.AI",
  url: URL,
  logo: {
    "@type": "ImageObject",
    url: OG_IMAGE,
  },
  image: OG_IMAGE,
  description:
    "ZNITH.AI — consultoria especializada em automação comercial com inteligência artificial. Metodologia própria, resultados reais, sem fórmula genérica.",
  founder: { "@id": PERSON_ID },
  areaServed: "BR",
  knowsLanguage: "pt-BR",
};

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: "SPRINT CODE",
  alternateName: "ZNITH.AI",
  url: URL,
  inLanguage: "pt-BR",
  description: DESCRIPTION,
  publisher: { "@id": ORG_ID },
};

const breadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "SPRINT CODE",
      item: URL,
    },
  ],
};

const personJsonLd = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": PERSON_ID,
  name: "Leilaine Campioto",
  jobTitle: "Fundadora",
  worksFor: { "@id": ORG_ID },
};

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_ITEMS.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
};

export const Route = createFileRoute("/")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
  }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "SPRINT CODE, IA para empresas, Claude Code para empresários, implantação individual de IA, agentes de IA",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(serviceJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(courseJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(orgJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(websiteJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(personJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd) },
    ],
  }),
  component: Index,
});

function Index() {
  return (
    <main className="relative min-h-screen overflow-x-hidden bg-midnight pb-20 text-foreground md:pb-0">
      <Navbar />
      <HeroSection />
      <div id="perf-problem" className="deferred-section cv-problem"><ProblemSection /></div>
      <div id="perf-promise" className="deferred-section cv-promise"><PromiseSection /></div>
      <div id="perf-sessions" className="deferred-section cv-sessions"><SessionsSection /></div>
      <div id="perf-audience" className="deferred-section cv-audience"><AudienceSection /></div>
      <div id="perf-proof" className="deferred-section cv-proof"><SocialProofSection /></div>
      <div id="perf-pricing" className="deferred-section cv-pricing"><PricingSection /></div>
      <div id="perf-how" className="deferred-section cv-how"><HowItWorksSection /></div>
      <div id="perf-form" className="deferred-section cv-form"><FormSection /></div>
      <div id="perf-faq" className="deferred-section cv-faq"><FAQSection /></div>
      <div id="perf-final" className="deferred-section cv-final"><FinalCTASection /></div>
      <div id="perf-footer" className="deferred-section cv-footer"><Footer /></div>
      <StickyMobileCTA />
    </main>
  );
}
